export type TSFixMe = any;
import {
  FinancialDetails,
  TradeInDetails,
  Part,
} from "@/app/types/sale-log-types";
import {
  IconCalendarWeek,
  IconClipboardList,
  IconFileArrowRight,
  IconFileText,
  IconFileTextFilled,
  IconGauge,
  IconLayoutDashboard,
  IconList,
  IconPhone,
  IconSettings,
  IconUsers,
} from "@tabler/icons-react";
import React from "react";

export interface Page {
  permission?: string[];
  name: string;
  path: string;
  icon: React.ReactNode;
  badge?: string;
  mobileReady?: boolean;
}

// Nav paths are consumed by next/link in the sidebar: they must NOT include
// the "/v2" basePath (Next prepends it), and they match usePathname() output
// for active-item highlighting.
export const dashboardPage: Page = {
  name: "Dashboard",
  path: `/dashboard`,
  icon: <IconLayoutDashboard size={20} stroke={1.5} />,
  mobileReady: true,
};

export const salesLogNew: Page = {
  name: "Sales Log",
  path: `/sales-log`,
  icon: <IconList size={20} stroke={1.5} />,
  permission: [
    "VIEW_SALES_LOG_FULL",
    "VIEW_SALES_LOG",
    "VIEW_SALES_LOG_PARTIAL",
  ],
  mobileReady: true,
};
export const salesLogSalesPersonNew: Page = {
  name: "Sales Log",
  path: `/sales-log`,
  icon: <IconList size={20} stroke={1.5} />,
  permission: ["VIEW_SALES_LOG_PARTIAL", "VIEW_SALES_LOG"],
  mobileReady: true,
};

export const deliveryCalendarNew: Page = {
  name: "Scheduled Deliveries",
  path: `/delivery-calendar`,
  icon: <IconCalendarWeek size={20} stroke={1.5} />,
  permission: ["VIEW_DELIVERY_SCHEDULE"],
  mobileReady: true,
};
export const newSaleNew: Page = {
  name: "New Sale",
  path: `/view-sale`,
  icon: <IconFileText size={20} stroke={1.5} />,
  permission: ["CREATE_NEW_SALE"],
};
export const usersNewPage: Page = {
  name: "Users",
  path: `/users`,
  icon: <IconUsers size={20} stroke={1.5} />,
  permission: ["VIEW_USERS"],
};

export const settingsV2Page: Page = {
  name: "Settings",
  path: `/settings`,
  icon: <IconSettings size={20} stroke={1.5} />,
  permission: ["ACCESS_CONFIGURATION"],
};

export const newDueBills: Page = {
  name: "Due Bills",
  path: `/due-bills`,
  icon: <IconClipboardList size={20} stroke={1.5} />,
  permission: ["ACCESS_DUEBILLS"],
};

export const leadsPage: Page = {
  name: "Leads",
  path: `/leads`,
  icon: <IconPhone size={20} stroke={1.5} />,
  permission: ["VIEW_LEADS", "EDIT_LEADS"],
};

export type LeadPersonType = "SALES_MANAGER" | "SALESPERSON";

export type LeadEntry = {
  _id: string;
  dealershipId: string;
  personName: string;
  personType: LeadPersonType;
  date: string;
  calls: number;
  emails: number;
  appointmentsShown: number;
  appointmentsSold: number;
  walkIns: number;
  createdAt: string;
  updatedAt: string;
  enteredByUserId: string;
};

export type LeadTableRow = {
  personName: string;
  calls: number;
  emails: number;
  appointmentsShown: number;
  appointmentsSold: number;
  walkIns: number;
  aptsPerDay: number;
  apptConversionRate: number;
};
export type DealershipPages = {
  [key: string]: Page;
};

export const defaultDealershipPages: DealershipPages = {
  dashboard: dashboardPage,
  "new-sale": newSaleNew,
  "sales-log": salesLogNew,
  "delivery-calendar": deliveryCalendarNew,
  users: usersNewPage,
  duebill: newDueBills,
  configure: settingsV2Page,
};

export type MobileTheme = {
  LIGHT: string;
  MED: string;
  MED2: string;
  SELECTED: string;
  DARK: string;
  EXTRALIGHT: string;
  TRACKEDGREEN: string;
  SLIDE: string;
  BACKGROUND: string;
  SHADOW: string;
  FONT1: string;
  FONT2: string;
  FONT3: string;
  FONTWHITE: string;
  NEW: string;
  USED: string;
};

export type Delivery = {
  id: number;
  scheduledDeliveryDate: string;
  delivered: boolean;
  stockNumber: string;
  status: string;
  carDescription: string;
  customer: string;
  salesPerson: string;
  saleId: string;
};
export type DeliveryWithExtras = {
  delivery: CalDelivery;
  vin: string;
  fsmManagerName: string;
};

export type CalDelivery = {
  carDescription: string;
  createdTimestamp: string;
  customer: string;
  dealershipId: string;
  delivered: boolean;
  id: string;
  saleId: string;
  salesPerson: string;
  scheduledDeliveryDate: string;
  status: string;
  stockNumber: string;
  vin?: string;
  fsmManagerName?: string;
};

export type SalesBreakdownData = {
  [key: string]: {
    units: number;
    frontGross: number;
    backGross: number;
  };
};

export type SalesOverviewOptions = {
  label: string;
  value: string;
};

export type BarChartOptions = {
  amount: number;
  key: string;
  percentage: number;
  color: TSFixMe;
}[];

export const dealStatusColors = {
  PENDING: "#f4f4f4",
  FACTORY_ORDERED: "#fffc5c",
  BOOKED: "#42f58d",
  FINALIZED: "#00c6c6",
  CANCELLED: "#ff0000",
};

export const saleTypeColors = {
  RETAIL: "#00c6c6",
  WHOLESALE: "#fc7c27",
  WHOLESALE_DIRECT: "#ff0000",
  FLEET: "#4877f8",
  CREDIT_SOLUTIONS: "#42f58d",
  DLA: "#9238f3",
};

export const dealTypeColors = {
  CASH: "#fdae3f",
  LEASE: "#42f58d",
  FINANCE: "#00c6c6",
};

export type Sale = {
  id: string;
  saleDate: Date;
  stockNumber: string;
  vehicle: string;
  dealNumber: string;
  fiGross: number;
  retailGross: number;
  saleStatus: string;
  customer: string;
  deliveryStatus: string;
  scheduledDeliveryDate: string;

  year: string;
  make: string;
  model: string;
  trim: string;
  salesPersonName: string;
  salesManagerName: string;
  fsmManagerName: string;

  age: string;
  bank: string;
  gtg: boolean;
  isATradeIn: boolean;
  cashConverted: boolean;
  saleType: string;
  dealershipId: string;

  financialDetails?: FinancialDetails;
  tradeInDetails?: TradeInDetails | null;
  secondTradeIn?: TradeInDetails | null;
};
export type Lender = {
  name: string;
};

export type FinanceFieldType = "TEXT" | "NUMBER" | "DROPDOWN" | "BOOLEAN";

export type FinanceFieldCategory =
  | "CREDIT_INSURANCE"
  | "WARRANTIES"
  | "VEHICLE_PROTECTION"
  | "FINANCIAL_INFORMATION";

export interface FinanceProduct {
  label: string;
  key: string;
  type: FinanceFieldType;
  category: FinanceFieldCategory;
  includeInGrossTotal?: boolean;
  options?: FinanceProductOption[];
}

export interface FinanceProductOption {
  label: string;
  value: string;
}

export type DealershipConfiguration = {
  backendGPUTarget: number;
  backendGrossTarget: number;
  businessDays: number;
  commentNotificationEmailEnabled: boolean;
  createdTimestamp: string; // ISO 8601 format date string
  dailyRecapEmailEnabled: boolean;
  dailyRecapEmailUserList: string[];
  dueBillNotificationEmailEnabled: boolean;
  frontendGPUTarget: number;
  grossChangeEmailUserList: string[];
  grossChangeNotificationEmailEnabled: boolean;
  grossChangeThreshold: number;
  houseDealsEnabled: boolean;
  lenders: Array<Lender>;
  models: {
    [make: string]: string[];
  };
  products: Array<{ [key: string]: TSFixMe }>;
  salesGrossTarget: number;
  specializedBusinessUnits: string[];
  timezone: string;
  unitsSoldTarget: number;
  useCustomVehicleDB: boolean;
  financeProducts?: Array<FinanceProduct>;
  isCustomFinanceEnabled: boolean;
  _id: string;
  customUnitTypes?: Record<string, string> | null;
};

export type DealershipInvitation = {
  _id: string;
  dealershipId: string;
  token: string;
  expiry: Date;
  groupId: string;
};

export interface Comment {
  id: string;
  message: string;
  saleId: string | null;
  createdTimestamp: string;
  dealershipId: string | null;
  userId: string;
  userName: string;
}

export type CancellationData = {
  _id: string;
  saleId: string;
  cancellationReason: string;
  createdTimestamp: Date;
  dealershipId: string;
  salesDetails: {
    _id: string;
    saleDate: Date;
    stockNumber: string;
    salesPersonName: string;
    salesManagerName: string;
    fsmManagerName: string;
    retailGross: number;
    fiGross: number;
    customer: string;
    vehicle: string;
  };
};

export type SaleData = {
  amortization: string;
  store: string | null;
  saleStatus: string;
  vehicleCondition: string;
  backendGrossTotal: number;
  bank: string | null;
  carryOver: boolean;
  cashConversion: boolean;
  customer: string;
  dealDate: string;
  dealNumber: string;
  fsmManager: string;
  make: string;
  model: string;
  paymentType: "cash" | "finance" | "lease";
  retailGross: number;
  saleType: string;
  salesManager: string;
  salesPerson: string;
  stockNumber: string;
  unitType: string;
  vin: string;
  year: string | null;
  isThisATradeIn: boolean;
  financialDetails: FinancialDetails;
  tradeInDetails: TradeInDetails | null;
  color: string | null;
  trim: string | null;
  type: string | null;
  vehicle: string;
  gender: string | null;
  grade: string | null;
  postalCode: string | null;
  gtg: boolean;
  age: number | null;
  daysInStock: number;
  cancellationReason: string | null;
  deliveryStatus: string;
  scheduledDeliveryDate: string | null;
};

export type NewSaleRequest = {
  amortization: string;
  backendGross: number;
  bank: string;
  carryOver: boolean;
  cashConverted: boolean;
  customer: string;
  data: SaleData;
  dealDate: string;
  dealNumber: string;
  financialDetails: FinancialDetails;
  fsmManager: string;
  isThisATradeIn: string;
  make: string;
  model: string;
  reportedStatus: string | null;
  retailGross: number;
  saleStatus: string;
  saleType: string;
  salesManager: string;
  salesPerson: string;
  stockNumber: string;
  store: string;
  tradeInDetails: TSFixMe | null;
  unitType: string;
  vehicleCondition: string;
  vin: string;
  year: string;
  dueBillRequest?: DueBill;
  secondTradeIn: TradeInDetails | null;
  customModel: string;
};

export interface DueBill {
  dueBillId: string;
  dueBillNumber: string;
  subject: string;
  dueDate: string;
  customerName: string;
  stockNumber: string;
  salesPersons: string[];
  salesManagers: string[];
  financeManagers: string[];
  serviceAdvisors: string[];
  partsAdvisors: string[];
  dueBillParts?: Part[];
  comments?: Comment[] | string;
}
export type User = {
  permissions: string[];
  name: string;
  email: string;
  id: string;
  userGroupIds: string[];
  needsGoalsSet: boolean;
  lastSignInAt: string;
  lastActiveAt: string;
  createdAt: string;
};

export type ClerkUser = {
  id: string;
  passwordEnabled: boolean;
  totpEnabled: boolean;
  backupCodeEnabled: boolean;
  twoFactorEnabled: boolean;
  banned: boolean;
  locked: boolean;
  createdAt: number;
  updatedAt: number;
  imageUrl: string;
  hasImage: boolean;
  primaryEmailAddressId: string;
  primaryPhoneNumberId: string | null;
  primaryWeb3WalletId: string | null;
  lastSignInAt: string | null;
  externalId: string;
  username: string | null;
  firstName: string;
  lastName: string;
  publicMetadata: object;
  privateMetadata: object;
  unsafeMetadata: object;
  emailAddresses: [];
  phoneNumbers: [];
  web3Wallets: [];
  externalAccounts: [];
  samlAccounts: [];
  lastActiveAt: string | null;
  createOrganizationEnabled: boolean;
  createOrganizationsLimit: number | null;
  deleteSelfEnabled: boolean;
};

export type Dealership = {
  name: string;
  key: string;
  id: string;
  _id?: string;
  active: boolean;
  dealershipConfiguration: DealershipConfiguration;
  featureOverrides?: Record<string, boolean>;
  dealershipLogo?: string;
};

export type TradeIn = {
  id: string;
  year: number;
  make: string;
  model: string;
  color: string;
  kms: number;
  acv: number;
  deliveryDate: string;
};
